import cn from 'classnames';
import { useContext } from 'react';
import { useMatch } from 'react-router';

import { HOME_CHEF, BrandContext } from 'context/BrandContext';
import * as Routes from 'helpers/routes';
import useFunnelMealPlan from 'hooks/useFunnelMealPlan';
import usePartnershipQuery from 'hooks/usePartnershipQuery';
import useQuery from 'hooks/useQuery';
import useScreenSizeConstants from 'hooks/useScreenSizeConstants';
import { FAMILY, TEMPO } from 'lib/constants';
import { PARTNERSHIP_RESOURCE_TYPES } from 'lib/constants/signUp';
import styles from 'styles/components/signUpFunnel/BackgroundContainer';

const getPartnerReferrer = () => {
  const reactFunnelElement = document?.getElementById('hc-sign-up-funnel-react-app');
  return reactFunnelElement?.getAttribute('data-partner-referrer');
};

const BRAND_INTRO_BACKGROUND = {
  [HOME_CHEF]: {
    gradient: 'linear-gradient(138deg, #fffcf9 17.43%, #fef9f5 38.93%, #faf0e8 74.54%)',
    leftPartnershipImageSize: '350px auto',
    leftPartnershipImagePosition: 'top 276px left -40px',
    rightPartnershipImageStyle: {
      top: '36px',
      right: '-50px',
      width: '350px',
      height: 'auto',
    },
  },
  [TEMPO]: {
    gradient: 'linear-gradient(141deg, #ffffff 17.91%, #f8f8f8 46.31%, #ffffff 71.18%)',
    leftPartnershipImageSize: '286px auto',
    leftPartnershipImagePosition: 'top 20% left -5%',
    rightPartnershipImageStyle: {
      top: '-3%',
      right: '-8%',
      width: '325px',
      height: 'auto',
    },
  },
};

const BackgroundContainer = ({ children }) => {
  const partnerReferrer = getPartnerReferrer();
  const { brand } = useContext(BrandContext);
  const { isQueryParamTrue: isAdSource } = useQuery('source', 'ad');
  const mealPlan = useFunnelMealPlan();
  const isFamilyPlan = mealPlan === FAMILY;

  const { isMobile, isMobileOrTablet } = useScreenSizeConstants();
  const brandRoutes = Routes.getBrandRoute({ brand, isFamily: isFamilyPlan });

  const isIntro = useMatch(brandRoutes.intro);
  const isPreferenceDietary = useMatch(brandRoutes.preferenceDietary);
  const isPreferenceAvoid = useMatch(brandRoutes.preferenceAvoid);
  const isPromo = useMatch(brandRoutes.promo);
  const isBox = useMatch(brandRoutes.box);
  const boxRecommenderPeople = useMatch(brandRoutes.boxRecommenderPeople);
  const boxRecommenderMeals = useMatch(brandRoutes.boxRecommenderMeals);
  const boxRecommenderDays = useMatch(brandRoutes.boxRecommenderDays);

  const { data: partnershipData } = usePartnershipQuery(partnerReferrer);
  const partnershipImageName = partnershipData?.partnership?.name;

  const introImages = partnershipData?.partnership?.resources.flatMap(resource =>
    resource.resourceType === PARTNERSHIP_RESOURCE_TYPES.INTRO_IMAGE ? [resource.url] : []
  );
  const isBoxRecommenderEnabled = Boolean(
    boxRecommenderPeople || boxRecommenderMeals || boxRecommenderDays
  );
  const isThankYou = useMatch(brandRoutes.thankyou);
  const {
    gradient: introGradient,
    leftPartnershipImageSize,
    leftPartnershipImagePosition,
    rightPartnershipImageStyle,
  } = BRAND_INTRO_BACKGROUND[brand] || BRAND_INTRO_BACKGROUND[HOME_CHEF];
  const isTempo = brand === TEMPO;
  const shouldShowIntroPartnershipImages =
    isIntro && !isTempo && !isAdSource && introImages?.length;
  const shouldShowRightPartnershipOverlayImage =
    shouldShowIntroPartnershipImages && !isMobileOrTablet;

  const backgroundClasses = cn('relative flex grow flex-col overflow-x-hidden', {
    'pb-6': !isThankYou,
    [styles[brand]]: brand,
    [styles.adSource]: isIntro && isAdSource,
    [styles.intro]: isIntro,
    [styles.preferences]: isPreferenceDietary || isPreferenceAvoid,
    [styles.preferencesFamily]: (isPreferenceDietary || isPreferenceAvoid) && isFamilyPlan,
    [styles.promo]: isPromo,
    [styles.promoFamily]: isPromo && isFamilyPlan,
    [styles.box]: isBox,
    [styles.boxRecommender]: isBoxRecommenderEnabled,
    [styles.thankYou]: isThankYou,
  });

  let backgroundStyle = {};

  if (isIntro && isAdSource) {
    backgroundStyle = { backgroundImage: introGradient };
  } else if (shouldShowIntroPartnershipImages) {
    if (isMobile) {
      backgroundStyle = { backgroundImage: introGradient };
    } else {
      backgroundStyle = {
        backgroundImage: `url('${introImages[0] || ''}'), ${introGradient}`,
        backgroundRepeat: 'no-repeat, no-repeat',
        backgroundSize: `${leftPartnershipImageSize}, auto`,
        backgroundPosition: `${leftPartnershipImagePosition}, top center`,
      };
    }
  }

  return (
    <div className={backgroundClasses} style={backgroundStyle}>
      {children}
      {shouldShowRightPartnershipOverlayImage && introImages[1] && (
        <img
          alt={partnershipImageName || 'Partnership Image'}
          src={introImages[1]}
          style={{
            position: 'absolute',
            zIndex: 1,
            pointerEvents: 'none',
            ...rightPartnershipImageStyle,
          }}
        />
      )}
    </div>
  );
};

BackgroundContainer.propTypes = {
  children: PropTypes.node.isRequired,
};

export default BackgroundContainer;
